import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Paper,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import api from "../utils/api"

export function EditEventForm() {
  const navigate = useNavigate()
  const [eventDescription, setEventDescription] = useState("")
  const [selectedEvent, setSelectedEvent] = useState("")
  const [eventName, setEventName] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [numVoters, setNumVoters] = useState<number>()
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [events, setEvents] = useState<EventSummary[]>([])
  const [openDialog, setOpenDialog] = useState(false)
  const [eventId, setEventId] = useState("")

  //Testing
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const handleDelete = (eventId: string) => {
    if (!eventId) {
      return
    }

    setEventId(eventId)
    setIsDeleteConfirmationOpen(true)
  }
  const handleConfirmDelete = async () => {
    setIsDeleteConfirmationOpen(false)

    if (!eventId) {
      return
    }

    try {
      await api.delete(`/admin/events/${eventId}`)
      navigate("/admin/dashboard", {
        state: { notification: "Event successfully deleted!" },
      })
    } catch (error) {
      console.error("Error deleting event:", error)
    }
  }

  type EventSummary = {
    name: string
    id: string
    endDate: { _seconds: number; _nanoseconds: number }
    startDate: { _seconds: number; _nanoseconds: number }
    archiveStatus: boolean
  }

  const convertToDate = (rawDate: {
    _seconds: number
    _nanoseconds: number
  }): Date => {
    return new Date(rawDate._seconds * 1000)
  }

  // useEffect for fetching active events
  useEffect(() => {
    api
      .get("/admin/events")
      .then((response) => {
        setEvents(response.data)
      })
      .catch((error) => {
        console.error("Error fetching active events:", error)
      })
  }, [])

  // useEffect for fetching selected event's data
  useEffect(() => {
    if (selectedEvent) {
      api
        .get(`/admin/events/${selectedEvent}`)
        .then((response) => {
          const eventData = response.data
          setEventName(eventData.name)
          setEventDescription(eventData.description)

          // Code to convert the startDate from UTC to the user's time in their time zone.
          const utcStartDate = new Date(eventData.startDate)
          const localStartDateStr = new Date(utcStartDate.toLocaleString())
          const localStartDate = new Date(localStartDateStr)
          localStartDate.setMinutes(
            localStartDate.getMinutes() - localStartDate.getTimezoneOffset()
          )
          const localStartDateISOString = localStartDate
            .toISOString()
            .slice(0, 16)

          // Code to convert the endDate from UTC to the user's time in their time zone.
          const utcEndDate = new Date(eventData.endDate)
          const localEndDateStr = new Date(utcEndDate.toLocaleString())
          const localEndDate = new Date(localEndDateStr)
          localEndDate.setMinutes(
            localEndDate.getMinutes() - localEndDate.getTimezoneOffset()
          )
          const localEndDateISOString = localEndDate.toISOString().slice(0, 16)

          setStartDate(localStartDateISOString)
          setEndDate(localEndDateISOString)
          setNumVoters(eventData.numVoters)
        })
        .catch((error) => {
          console.error("Error fetching selected event data:", error)
        })
    }
  }, [selectedEvent])

  const handleRowClick = (event: EventSummary) => {
    setSelectedEvent(event.id)
    setOpenDialog(true)
  }

  const handleSubmit = async () => {
    if (!selectedEvent) return

    if (!eventName || !startDate || !endDate || !numVoters) {
      setErrorMessage("All fields are required")
      setOpenSnackbar(true)
      return
    }

    const currentDateTime = new Date()
    const startDateObj = new Date(startDate)
    const endDateObj = new Date(endDate)

    if (currentDateTime > startDateObj) {
      setErrorMessage("Start date is before today.")
      setOpenSnackbar(true)
      return
    }
    if (startDateObj > endDateObj) {
      setErrorMessage("End date is before start date.")
      setOpenSnackbar(true)
      return
    }

    if (numVoters > 1000) {
      setErrorMessage("Voter Limit Maximum is 1000")
      setOpenSnackbar(true)
      return
    }

    try {
      // Construct JSON payload
      const eventPayload = {
        name: eventName,
        description: eventDescription,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        numVoters: numVoters.toString(),
        // image: eventImage,
      }

      const putResponse = await api.put(
        `/admin/events/${selectedEvent}`,
        eventPayload, // Sending JSON payload
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (putResponse.status === 200) {
        navigate("/admin/dashboard", {
          state: { notification: "Event successfully edited" },
        })
      } else {
        console.error("Failed to update event")
      }
    } catch (error) {
      console.error("Error updating event:", error)
    }
    setOpenDialog(false)
  }

  return (
    <>
      <Typography variant="h6" m={4}>
        Manage Event
      </Typography>

      {/* Events Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell>Event ID</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(events) && events.length > 0 ? (
              events.map((event) => {
                if (event.archiveStatus) return null // Skip archived events
                return (
                  <TableRow key={event.id}>
                    <TableCell>{event.name}</TableCell>
                    <TableCell>{event.id}</TableCell>
                    <TableCell>
                      {convertToDate(event.startDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {convertToDate(event.endDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        aria-label="Edit"
                        onClick={() => handleRowClick(event)}
                        sx={{ marginRight: 1 }}
                      >
                        <EditIcon />
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        aria-label="Delete"
                        onClick={() => handleDelete(event.id)}
                      >
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No event data available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth="xl"
      >
        <DialogTitle id="form-dialog-title">Edit Event</DialogTitle>

        <DialogContent>
          <TextField
            label="Event Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={eventName}
            required
            onChange={(e) => setEventName(e.target.value)}
          />

          <TextField
            label="Event Description"
            variant="outlined"
            fullWidth
            margin="normal"
            value={eventDescription}
            required
            onChange={(e) => setEventDescription(e.target.value)}
          />

          <TextField
            label="Number of Voters"
            type="number"
            variant="outlined"
            fullWidth
            margin="normal"
            value={numVoters}
            required
            inputProps={{ min: 1 }}
            onChange={(e) => setNumVoters(parseInt(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Start Date"
            type="datetime-local"
            variant="outlined"
            fullWidth
            margin="normal"
            value={startDate}
            required
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label="End Date"
            type="datetime-local"
            variant="outlined"
            fullWidth
            margin="normal"
            value={endDate}
            required
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start of Delete single event */}
      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this event?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteConfirmationOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
